import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { useCallback, useState } from 'react';
import type { Territory } from '../../types/product';
import { useTranslation } from '../../i18n';
import { AGREEMENT_PILL_CLASSES, type AgreementKey } from '../../utils/agreementStyles';

interface Props {
  territory: Territory;
  showName?: boolean;
}

export default function TerritoryAgreementPill({ territory, showName = false }: Props) {
  const { t } = useTranslation();
  const key: AgreementKey = territory.agreementType ?? 'none';
  const colorClass = AGREEMENT_PILL_CLASSES[key];
  const label = showName ? territory.name : territory.code;
  const agreementLabel = t.filterSidebar.agreements[key];
  const description = t.filterSidebar.agreementDescriptions[key];

  const [open, setOpen] = useState(false);
  // Portal + shift/flip so the tooltip escapes the product modal's
  // overflow-hidden clip (VIVATISPDB-300). Anchored to the top by default,
  // flips to the bottom if there is not enough room, and shifts
  // horizontally so it always stays inside the viewport no matter where
  // the pill sits within the modal.
  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'top',
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  // Wrap the floating-ui setters in useCallback so the linter's
  // react-hooks/refs rule accepts them as callback refs rather than
  // mistakenly flagging property access on a ref-shaped object.
  const attachReference = useCallback(
    (node: HTMLSpanElement | null) => refs.setReference(node),
    [refs],
  );
  const attachFloating = useCallback(
    (node: HTMLDivElement | null) => refs.setFloating(node),
    [refs],
  );

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  return (
    <>
      <span
        ref={attachReference}
        {...getReferenceProps()}
        className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap ${colorClass}`}
      >
        {label}
        {territory.agreementType && (
          <span className="font-semibold">{territory.agreementType}</span>
        )}
        {territory.inReview && (
          <span className="opacity-70" aria-label="in review">⏳</span>
        )}
      </span>
      {open && (
        <FloatingPortal>
          <div
            ref={attachFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="pointer-events-none z-[100] max-w-[min(18rem,calc(100vw-2rem))] rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg dark:bg-gray-700"
          >
            <span className="block font-semibold">{territory.name} — {agreementLabel}</span>
            <span className="block font-normal opacity-80">{description}</span>
            {territory.inReview && (
              <span className="block font-normal italic opacity-80">(in review)</span>
            )}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
